/**
 * FlowDebugPanel.tsx -- MER Algo Footprints v9.2.0
 *
 * Phase 1 hardening: display-only. Flow fetching/polling is now driven by
 * InstitutionalFlowRuntime in AppShell. This component subscribes to the
 * sidecar (promptBuilder.subscribeFlowContext) and renders the current
 * signal for the active symbol.
 *
 * Feature-gated by REACT_APP_FLOW_DEBUG_ENABLED. Defaults to false in
 * production; set 'true' in dev environments only.
 *
 * Design notes (preserved from prior version):
 *   - z-index 998 keeps it under modals/dialogs (typical 1000+) but above
 *     workspace content.
 *   - Width 320px, max-height 480px with internal scroll.
 *   - Inline styles to match the rest of the app's brutalist styling and
 *     avoid touching the shared stylesheet for a debug-only feature.
 */

import React, { useEffect, useState } from 'react';
import { useStore } from '../../store';
import {
  getLiveFlowContext,
  subscribeFlowContext,
} from '../../domains/ai/prompts/promptBuilder';
import type { FlowBias, FlowSignal } from '../../domains/institutionalFlow/schemas';

const ENABLED =
  (process.env.REACT_APP_FLOW_DEBUG_ENABLED ?? 'false').toLowerCase() === 'true';

// ── Color mapping for bias ──────────────────────────────────────────────────
function biasColor(bias: FlowBias): string {
  switch (bias) {
    case 'bullish_accumulation': return '#22D67A';
    case 'bearish_distribution': return '#E94B4B';
    case 'neutral':              return '#C9A84C';
    case 'no_signal':            return '#3A5070';
    default:                     return '#3A5070';
  }
}

function biasLabel(bias: FlowBias): string {
  switch (bias) {
    case 'bullish_accumulation': return 'BULL ACCUM';
    case 'bearish_distribution': return 'BEAR DIST';
    case 'neutral':              return 'NEUTRAL';
    case 'no_signal':            return 'NO SIGNAL';
    default:                     return String(bias).toUpperCase();
  }
}

function formatAge(ms: number | null): string {
  if (ms == null) return '--';
  const age = Date.now() - ms;
  if (age < 0) return 'now';
  if (age < 5_000) return 'now';
  if (age < 60_000) return `${Math.floor(age / 1000)}s ago`;
  return `${Math.floor(age / 60_000)}m ago`;
}

// ── Component ───────────────────────────────────────────────────────────────
export function FlowDebugPanel() {
  const [open, setOpen] = useState(false);
  const activeSymbol = useStore(s => s.activeSymbol);

  // Local snapshot of the sidecar entry for the active symbol. Re-reads on
  // every emit from the sidecar; the runtime drives the actual polling.
  const [signal, setSignal] = useState<FlowSignal | null>(null);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);

  useEffect(() => {
    if (!activeSymbol) {
      setSignal(null);
      setLastUpdated(null);
      return;
    }
    const sym = activeSymbol.toUpperCase();
    // Prime from current sidecar state
    const initial = getLiveFlowContext(sym);
    setSignal(initial);
    setLastUpdated(initial ? Date.now() : null);

    const unsub = subscribeFlowContext((changedSym) => {
      if (changedSym !== sym) return;
      const next = getLiveFlowContext(sym);
      setSignal(next);
      setLastUpdated(next ? Date.now() : null);
    });
    return unsub;
  }, [activeSymbol]);

  if (!ENABLED) return null;

  return (
    <div
      style={{
        position: 'fixed',
        right: 12,
        bottom: 40,
        width: open ? 320 : 'auto',
        maxHeight: open ? 480 : 'auto',
        background: '#0E1A2D',
        border: '1px solid #1A3050',
        borderRadius: 4,
        color: '#9CB0CC',
        fontFamily: "'Courier New', monospace",
        fontSize: 11,
        zIndex: 998,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
      }}
    >
      {/* Header / toggle */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          all: 'unset',
          padding: '6px 10px',
          cursor: 'pointer',
          color: open ? '#C9A84C' : '#9CB0CC',
          fontWeight: 700,
          letterSpacing: 0.5,
          textAlign: 'left',
          borderBottom: open ? '1px solid #1A3050' : 'none',
        }}
        title="Toggle institutional flow debug panel"
      >
        {open ? '▼' : '▶'} INSTITUTIONAL FLOW {open && activeSymbol ? ` -- ${activeSymbol}` : ''}
      </button>

      {/* Body */}
      {open && (
        <div style={{ padding: '8px 10px', overflowY: 'auto' }}>
          {!activeSymbol && (
            <div style={{ color: '#3A5070' }}>No active symbol</div>
          )}

          {activeSymbol && !signal && (
            <div style={{ color: '#3A5070' }}>waiting for first poll...</div>
          )}

          {activeSymbol && signal && (
            <>
              {/* Bias + confidence */}
              <div style={{ marginBottom: 8 }}>
                <span style={{ color: biasColor(signal.bias), fontWeight: 700 }}>
                  {biasLabel(signal.bias)}
                </span>
                <span style={{ float: 'right', color: '#9CB0CC' }}>
                  conf {(signal.confidence * 100).toFixed(0)}%
                </span>
              </div>

              {/* Metrics */}
              <div style={{ marginBottom: 8, lineHeight: 1.5 }}>
                <div>off-exch: {(signal.metrics.offExchangePct * 100).toFixed(1)}% ({signal.metrics.offExchangeVolume.toLocaleString()} / {signal.metrics.totalVolume.toLocaleString()})</div>
                <div>blocks: {signal.metrics.blockCount} (largest {signal.metrics.largestBlock.toLocaleString()})</div>
                <div>vwap: {signal.metrics.vwap.toFixed(2)} | last: {signal.metrics.lastPrice.toFixed(2)} ({signal.metrics.vwapDelta >= 0 ? '+' : ''}{(signal.metrics.vwapDelta * 100).toFixed(2)}%)</div>
                <div>trades: {signal.metrics.tradeCount} in {((signal.windowMs.to - signal.windowMs.from) / 60000).toFixed(0)}min</div>
              </div>

              {/* Evidence */}
              {signal.evidence.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ color: '#3A5070', marginBottom: 2 }}>evidence:</div>
                  <ul style={{ margin: 0, paddingLeft: 14 }}>
                    {signal.evidence.map((e, i) => (
                      <li key={i} style={{ marginBottom: 2 }}>{e}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Footer / age */}
              <div style={{ color: '#3A5070', fontSize: 10, paddingTop: 4, borderTop: '1px solid #1A3050' }}>
                updated {formatAge(lastUpdated)}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
